import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Eye, EyeOff } from 'lucide-react';
import { validateResetToken, resetPassword } from '@/api/user.api';
import { useToast } from '@/contexts/toast-context';

import { Card, CardContent, Button, Label, Input } from '@/components/ui';
import { Spinner } from '../../../components';

import './reset-password-style.scss';
import { TOAST_TYPES } from '@/constants/common.constants';

export const Route = createFileRoute('/reset-password/$token/')({
  component: ResetPassword,
});

function ResetPassword() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});

  useEffect(() => {
    const validateToken = async () => {
      try {
        setIsValidating(true);
        const response = await validateResetToken(token);
        setUserName(response.name);
      } catch (err: unknown) {
        addToast(
          err instanceof Error ? err.message : 'Invalid or expired reset link',
          { type: TOAST_TYPES.ERROR },
        );
        navigate({ to: '/login' });
      } finally {
        setIsValidating(false);
      }
    };
    validateToken();
  }, []);

  const handleSubmit = async () => {
    // Validate password fields
    const newErrors: typeof errors = {};

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirm password is required';
    } else if (confirmPassword.length < 8) {
      newErrors.confirmPassword = 'Password must be at least 8 characters';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // Submit password reset
    try {
      setIsSubmitting(true);
      await resetPassword(token, password);
      addToast('Password reset successfully!', {
        type: TOAST_TYPES.SUCCESS,
      });
      navigate({ to: '/login' });
    } catch (err: unknown) {
      addToast(
        err instanceof Error ? err.message : 'Failed to reset password',
        { type: TOAST_TYPES.ERROR },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="reset-password-container">
      <Card className="reset-password-card">
        <CardContent>
          {isValidating ? (
            <div className="reset-password-loading">
              <Spinner size="medium" />
              <p>Validating reset link...</p>
            </div>
          ) : (
            <>
              <div className="reset-password-header">
                <img
                  src="/full-logo.svg"
                  alt="Logo"
                  className="reset-password-logo"
                />
                <h1>Reset Your Password</h1>
                <p className="reset-password-welcome">
                  Welcome back, {userName}!
                </p>
              </div>

              {/* Password */}
              <div className="reset-password-field">
                <Label>New Password</Label>
                <div className="reset-password-password-wrapper">
                  <Input
                    value={password}
                    type={showPassword ? 'text' : 'password'}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password) {
                        setErrors((prev) => ({ ...prev, password: undefined }));
                      }
                    }}
                    onBlur={() => {
                      if (!password) {
                        setErrors((prev) => ({
                          ...prev,
                          password: 'Password is required',
                        }));
                      } else if (password.length < 8) {
                        setErrors((prev) => ({
                          ...prev,
                          password: 'Password must be at least 8 characters',
                        }));
                      }
                      if (
                        confirmPassword &&
                        password === confirmPassword &&
                        errors.confirmPassword === 'Passwords do not match'
                      ) {
                        setErrors((prev) => ({
                          ...prev,
                          confirmPassword: undefined,
                        }));
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="basic-primary reset-password-password-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="invalid-feedback">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="reset-password-field">
                <Label>Confirm Password</Label>
                <div className="reset-password-password-wrapper">
                  <Input
                    value={confirmPassword}
                    type={showConfirmPassword ? 'text' : 'password'}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errors.confirmPassword) {
                        setErrors((prev) => ({
                          ...prev,
                          confirmPassword: undefined,
                        }));
                      }
                    }}
                    onBlur={() => {
                      if (!confirmPassword) {
                        setErrors((prev) => ({
                          ...prev,
                          confirmPassword: 'Confirm password is required',
                        }));
                      } else if (confirmPassword.length < 8) {
                        setErrors((prev) => ({
                          ...prev,
                          confirmPassword:
                            'Password must be at least 8 characters',
                        }));
                      } else if (password !== confirmPassword) {
                        setErrors((prev) => ({
                          ...prev,
                          confirmPassword: 'Passwords do not match',
                        }));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmit();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="basic-primary reset-password-password-toggle"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label="Toggle confirm password visibility"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="invalid-feedback">{errors.confirmPassword}</p>
                )}
              </div>

              <p className="help-text">
                Note: Password must be a minimum of 8 characters
              </p>

              <Button
                variant="basic-primary"
                className="reset-password-submit-button"
                size="medium"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting && <Spinner size="small" />}
                Reset Password
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
